/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {EventEmitter, Inject, Injectable} from '@angular/core';
import {KeycloakWrapperService} from './keycloak-wrapper.service';
import {from, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import {ObserveType, ResponseStatusCodes} from '../../app.constants';
import {isPresent} from '../utils/lang';


/**
 * Possible authentication events streamed by the authentication service
 */
export enum AuthenticationEvent {
  LOGIN_SUCCESSFUL,
  TOKEN_VERIFIED,
  LOGIN_ATTEMPT_FAILED,
  LOGOUT
}

/**
 * Authentication services that logs in and logs out users against the server.
 */
@Injectable()
export class AuthenticationService {

  private static TOKEN_LOCALSTORAGE_KEY: string = 'gp-token';

  public authenticationEvents: EventEmitter<any> = new EventEmitter<any>();

  private userLoggedIn: boolean = null;

  private userLoggedInPromise: any = null;

  constructor(private httpClient: HttpClient,
              @Inject('GP_API_HOST') private apiHost: string,
              private keycloakWrapperService: KeycloakWrapperService) {
  }

  /**
   * @returns {Promise<boolean>} promise resolving to true when a user is currently authenticated, to false otherwise
   */
  public isAuthenticated(): Promise<boolean> {
    if (this.userLoggedIn !== null) {
      return Promise.resolve(this.userLoggedIn);
    }
    if (!isPresent(this.userLoggedInPromise)) {
      this.userLoggedInPromise = this.checkAuthentication();
    }
    return this.userLoggedInPromise;
  }

  public checkAuthentication(): Promise<boolean> {
    return this.getToken()
      .pipe(
        switchMap((token: string) => {

          if (token) {
            let headers = new HttpHeaders();
            headers = headers.append('Authorization', `Bearer ${token}`);
            return this.httpClient
              .get(`${this.apiHost}/authent/api/v1/cliUser/me`, {headers, observe: ObserveType.RESPONSE})

              .pipe(
                map((result: any) => {
                  if (result.status === ResponseStatusCodes.OK) {
                    this.userLoggedIn = true;

                    this.authenticationEvents.next({event: AuthenticationEvent.TOKEN_VERIFIED, response: result});
                  } else {
                    this.userLoggedIn = false;
                    this.onAuthenticationError();
                  }

                  return this.userLoggedIn;
                }, error => {
                  this.onAuthenticationError();
                  this.userLoggedIn = false;
                  return this.userLoggedIn;
                }),
                catchError(() => of(false))
              );
          }

          this.onAuthenticationError();
          this.userLoggedIn = false;
          return of(this.userLoggedIn);
        })
      ).toPromise();
  }

  public onAuthenticationError(): void {
    this.keycloakWrapperService.logout();
  }

  public getToken(): Observable<string> {
    return from(this.keycloakWrapperService.getToken());
  }

  public refreshToken(): Observable<string> {
    return from(this.keycloakWrapperService.getToken(-1));
  }

  public removeToken(): void {
    localStorage.removeItem(AuthenticationService.TOKEN_LOCALSTORAGE_KEY);
  }

  /**
   * Logs out the user : delete its localStorage stored token and emit an logout event
   */
  public logout(): void {
    this.keycloakWrapperService.logout();
  }

  public login(path?: string): Observable<any> {
    return from(this.keycloakWrapperService.login({redirectUri: path}));
  }

  public clearUser() {
    this.userLoggedIn = false;
    this.removeToken();
    this.authenticationEvents.emit({event: AuthenticationEvent.LOGOUT});
  }
}
